import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { applyTheme, DEFAULT_THEME_ID, isThemeId, type ThemeId } from '@/theme';

/**
 * Two independent choices live in this store, and keeping them independent is
 * the point:
 *
 *   `appTheme`  WHICH theme — Ivory & Brass, Graphite & Azure, …
 *   `theme`     the APPEARANCE — light, dark, or follow the system
 *
 * Every combination is valid, so selecting a theme never disturbs a user's
 * light/dark preference and toggling dark never resets their theme.
 */

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  appTheme: ThemeId;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setAppTheme: (appTheme: ThemeId) => void;
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolvedTheme: getSystemTheme(),
      appTheme: DEFAULT_THEME_ID,

      setTheme: (theme) => {
        const resolvedTheme = resolveTheme(theme);
        applyTheme(get().appTheme, resolvedTheme);
        set({ theme, resolvedTheme });
      },

      toggleTheme: () => {
        const { resolvedTheme, appTheme } = get();
        const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
        applyTheme(appTheme, newTheme);
        set({ theme: newTheme, resolvedTheme: newTheme });
      },

      // Repaints in place — the tokens are custom properties on <html>, so
      // every screen follows without a reload and without remounting.
      setAppTheme: (appTheme) => {
        if (!isThemeId(appTheme)) return;
        applyTheme(appTheme, get().resolvedTheme);
        set({ appTheme });
      },
    }),
    {
      name: 'theme-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          // A value persisted by an older build has no `appTheme`, and one
          // persisted by a newer build may name a theme this bundle does not
          // ship. Both fall back rather than blanking the app.
          if (!isThemeId(state.appTheme)) state.appTheme = DEFAULT_THEME_ID;
          const resolvedTheme = resolveTheme(state.theme);
          applyTheme(state.appTheme, resolvedTheme);
          state.resolvedTheme = resolvedTheme;
        }
      },
    }
  )
);

/**
 * Paint the stored preferences before React's first render.
 *
 * `persist` rehydrates synchronously against localStorage, so by the time this
 * runs the store already holds the user's choices; calling it from main.tsx
 * ahead of `createRoot().render()` is what stops a themed user seeing one
 * frame of somebody else's palette.
 */
export function initializeTheme(): void {
  const { appTheme, theme } = useThemeStore.getState();
  applyTheme(appTheme, resolveTheme(theme));
  watchSystemTheme();
}

/**
 * Follow the OS colour scheme while the stored preference is still `system`.
 *
 * The preference is read when the event fires, not when the listener is
 * registered: a user who has since picked light or dark explicitly must not be
 * pulled back by the OS switching at sunset.
 */
let systemThemeWatched = false;
function watchSystemTheme(): void {
  if (systemThemeWatched || typeof window === 'undefined' || !window.matchMedia) return;
  systemThemeWatched = true;

  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme, setTheme } = useThemeStore.getState();
    if (theme === 'system') setTheme('system');
  });
}
